// TODO: Add Sentry error tracking to mutation hooks (deferred from PR #77)
// Customer mutations should have Sentry.captureException in onError handlers
// to match the pattern in cart.ts and checkout.ts for production debugging.
import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';

import { api } from '@lib/api/client';
import type {
  EnhancedStoreCustomer,
  EnhancedStoreCustomerAddress,
} from '@marxbiotech/shared-types/medusa';
import { accountKeys, type AccountLayoutData } from '@lib/query/options/account';
import { layoutKeys } from '@lib/query/options/layout';

// =============================================================================
// Cache Update Helpers
// =============================================================================

/**
 * Helper to safely update addresses in cached account layout data.
 * Handles undefined customer or addresses array.
 */
function updateCachedAddresses(
  oldData: AccountLayoutData | undefined,
  updater: (addresses: EnhancedStoreCustomerAddress[]) => EnhancedStoreCustomerAddress[]
): AccountLayoutData | undefined {
  if (!oldData?.customer) return oldData;
  const currentAddresses = oldData.customer.addresses ?? [];
  return {
    ...oldData,
    customer: {
      ...oldData.customer,
      addresses: updater(currentAddresses),
    },
  };
}

// =============================================================================
// API Response Types
// =============================================================================

interface CustomerMutationResponse {
  customer: EnhancedStoreCustomer;
}

// =============================================================================
// Update Customer
// =============================================================================

interface UpdateCustomerInput {
  first_name?: string;
  last_name?: string;
  phone?: string;
  metadata?: Record<string, unknown>;
}

type UseUpdateCustomerOptions = Omit<
  UseMutationOptions<EnhancedStoreCustomer, Error, UpdateCustomerInput>,
  'mutationFn'
>;

export function useUpdateCustomer(options?: UseUpdateCustomerOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: UpdateCustomerInput
    ): Promise<EnhancedStoreCustomer> => {
      const response = await api.patch<CustomerMutationResponse>(
        '/customers/me',
        data
      );
      return response.data.customer;
    },
    onSuccess: (...args) => {
      // Invalidate layout + account queries to refresh customer data
      queryClient.invalidateQueries({ queryKey: layoutKeys.all });
      queryClient.invalidateQueries({ queryKey: accountKeys.all });
      // Call user-provided onSuccess if exists
      options?.onSuccess?.(...args);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
}

// =============================================================================
// Logout
// =============================================================================

interface LogoutResponse {
  success: boolean;
}

type UseLogoutOptions = Omit<
  UseMutationOptions<LogoutResponse, Error, void>,
  'mutationFn'
>;

export function useLogout(options?: UseLogoutOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<LogoutResponse> => {
      const response = await api.post<LogoutResponse>('/auth/logout');
      return response.data;
    },
    onSuccess: (...args) => {
      // Clear all cached queries on logout
      queryClient.clear();
      // Call user-provided onSuccess if exists
      options?.onSuccess?.(...args);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
}

// =============================================================================
// Address Types
// =============================================================================

// Use EnhancedStoreCustomerAddress from shared-types to match the type
// used in EnhancedStoreCustomer.addresses
export type CustomerAddress = EnhancedStoreCustomerAddress;

interface AddressesResponse {
  addresses: EnhancedStoreCustomerAddress[];
}

/**
 * Input for creating a new address.
 * Required fields match BFF CreateAddressDto validation.
 * Taiwan addresses use last_name for full name (no first_name).
 */
export interface CreateAddressInput {
  /** Required: 收件人姓名 (Taiwan: full name in this field) */
  last_name: string;
  /** Required: 詳細地址 */
  address_1: string;
  /** Required: 城市/縣 */
  city: string;
  /** Required: 郵遞區號 + 區域 (format: "123 區域名") */
  postal_code: string;
  /** Required: 國家代碼 (default: "tw") */
  country_code: string;
  /** Optional: 藥局門市名稱 */
  company?: string;
  /** Optional: 地址第二行 */
  address_2?: string;
  /** Optional: 電話號碼 */
  phone?: string;
  /** Optional: 是否設為預設地址 */
  is_default_shipping?: boolean;
  /** Optional: 其他 metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Input for updating an existing address.
 * All address fields are optional for partial updates.
 */
export interface UpdateAddressInput {
  /** Required: Address ID to update */
  address_id: string;
  last_name?: string;
  address_1?: string;
  city?: string;
  postal_code?: string;
  country_code?: string;
  company?: string;
  address_2?: string;
  phone?: string;
  is_default_shipping?: boolean;
  metadata?: Record<string, unknown>;
}

// =============================================================================
// Create Address
// =============================================================================

type UseCreateAddressOptions = Omit<
  UseMutationOptions<CustomerAddress[], Error, CreateAddressInput>,
  'mutationFn'
>;

/**
 * Hook to create a new customer address.
 * Automatically updates the account cache on success.
 * Returns the full addresses array for cache consistency.
 */
export function useCreateAddress(options?: UseCreateAddressOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAddressInput): Promise<CustomerAddress[]> => {
      const response = await api.post<AddressesResponse>(
        '/customers/me/addresses',
        data
      );
      return response.data.addresses;
    },
    onSuccess: (addresses, ...rest) => {
      // Replace cached addresses with the full list from server
      const replaceAddresses = () => addresses;
      queryClient.setQueryData(
        accountKeys.layout(),
        (old: AccountLayoutData | undefined) => updateCachedAddresses(old, replaceAddresses)
      );
      options?.onSuccess?.(addresses, ...rest);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
}

// =============================================================================
// Update Address
// =============================================================================

type UseUpdateAddressOptions = Omit<
  UseMutationOptions<CustomerAddress[], Error, UpdateAddressInput>,
  'mutationFn'
>;

/**
 * Hook to update an existing customer address.
 * Automatically updates the account cache on success.
 * Returns the full addresses array for cache consistency (handles is_default_shipping updates).
 */
export function useUpdateAddress(options?: UseUpdateAddressOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateAddressInput): Promise<CustomerAddress[]> => {
      const { address_id, ...updateData } = data;
      const response = await api.patch<AddressesResponse>(
        `/customers/me/addresses/${address_id}`,
        updateData
      );
      return response.data.addresses;
    },
    onSuccess: (addresses, variables, ...rest) => {
      // Replace cached addresses with the full list from server
      const replaceAddresses = () => addresses;
      queryClient.setQueryData(
        accountKeys.layout(),
        (old: AccountLayoutData | undefined) => updateCachedAddresses(old, replaceAddresses)
      );
      options?.onSuccess?.(addresses, variables, ...rest);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
}

// =============================================================================
// Delete Address
// =============================================================================

interface DeleteAddressInput {
  address_id: string;
}

interface DeleteAddressResponse {
  success: boolean;
}

type UseDeleteAddressOptions = Omit<
  UseMutationOptions<DeleteAddressResponse, Error, DeleteAddressInput>,
  'mutationFn'
>;

/**
 * Hook to delete a customer address.
 * Automatically updates the account cache on success.
 */
export function useDeleteAddress(options?: UseDeleteAddressOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: DeleteAddressInput
    ): Promise<DeleteAddressResponse> => {
      const response = await api.delete<DeleteAddressResponse>(
        `/customers/me/addresses/${data.address_id}`
      );
      return response.data;
    },
    onSuccess: (result, variables, ...rest) => {
      // Remove deleted address from cache
      const removeAddress = (addresses: EnhancedStoreCustomerAddress[]) =>
        addresses.filter((addr) => addr.id !== variables.address_id);
      queryClient.setQueryData(
        accountKeys.layout(),
        (old: AccountLayoutData | undefined) => updateCachedAddresses(old, removeAddress)
      );
      options?.onSuccess?.(result, variables, ...rest);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
}
